import {
  formatParisDateTime,
  type RechargeFeatureMode,
  type RechargeSlackTrace,
} from '@flux/shared';
import { ExternalLink } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton.js';

/**
 * Briques communes des panneaux de détail de l'écran Abonnements (`06` §8) :
 * « — », sous-section titrée, lien vers l'admin Recharge, squelette à la
 * forme d'une fiche et bloc « Notification » (trace du message Slack).
 */

export function Dash(): JSX.Element {
  return <span className="text-muted-foreground">—</span>;
}

/** Squelette d'une fiche : lignes libellé → valeur puis un sous-bloc. */
export function DetailSkeleton({ label, rows = 6 }: { label: string; rows?: number }): JSX.Element {
  return (
    <div aria-busy="true" aria-label={label} className="flex flex-col gap-3">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={`detail-skeleton-${String(index)}`}
          className="grid grid-cols-1 gap-1 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-6"
        >
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-3.5 w-full max-w-56" />
        </div>
      ))}
      <Skeleton className="mt-2 h-4 w-24" />
      <Skeleton className="h-3.5 w-full max-w-72" />
    </div>
  );
}

/** Premier bloc du panneau : titre `h3` sans séparateur. */
export function DetailSummary({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

/** Sous-bloc du panneau : titre `h3` et séparateur, jamais un nouveau cadre. */
export function DetailSection({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="flex flex-col gap-3 border-t border-border pt-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

export function ExternalRechargeLink({ href, children }: { href: string; children: string }): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-sm text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
      <ExternalLink className="size-3.5" aria-hidden="true" />
    </a>
  );
}

function SlackPreview({ preview }: { preview: string }): JSX.Element {
  return (
    <pre className="max-h-[24rem] overflow-auto overscroll-contain whitespace-pre-wrap break-words rounded-md border border-border bg-muted p-3 font-mono text-xs text-foreground">
      {preview}
    </pre>
  );
}

/**
 * Trace du message Slack abonnements d'un élément (`06` §1.4) : envoyé (date
 * + message repliable), non délivré (erreur + message), sinon le message pas
 * encore envoyé (Actif) ou qui aurait été envoyé (Simulation).
 */
export function SlackNotification({
  slack,
  mode,
}: {
  slack: RechargeSlackTrace;
  mode: RechargeFeatureMode;
}): JSX.Element {
  if (slack.sentAt !== null) {
    return (
      <>
        <p className="text-sm">
          Envoyé sur Slack le{' '}
          <span className="whitespace-nowrap tabular-nums">{formatParisDateTime(new Date(slack.sentAt))}</span>
        </p>
        <details className="group">
          <summary className="cursor-pointer select-none rounded-sm text-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Message envoyé
          </summary>
          <div className="mt-2">
            <SlackPreview preview={slack.preview} />
          </div>
        </details>
      </>
    );
  }
  if (slack.error !== null) {
    return (
      <>
        <p className="text-sm text-status-red-fg">Message Slack non délivré : {slack.error}</p>
        <SlackPreview preview={slack.preview} />
      </>
    );
  }
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {mode === 'active' ? 'Message pas encore envoyé' : 'Message qui aurait été envoyé'}
      </p>
      <SlackPreview preview={slack.preview} />
    </>
  );
}
